import { APP } from '@advanza/advanza_generic'
import { Col, Row } from '@advanza/grid'
import Modal from '@advanza/modal'
import { Button, Divider, Icon, PreIcon } from '@advanza/ui'
import FaqsList from 'components/faqs/FaqsList'
import Checkbox from 'components/input/Checkbox'
import KeywordsList from 'components/keywords/KeywordsList'
import SystemLogsList from 'components/logs/SystemLogsList'
import AutoRow from 'components/misc/AutoRow'
import AddOptionPageButton from 'components/services/AddOptionPageButton'
import CrossSellOptionsDropdown from 'components/services/CrossSellOptionsDropdown'
import CtasList from 'components/services/CtasList'
import GenerateTop10EmailWidgetsButton from 'components/services/GenerateTop10EmailWidgetsButton'
import InvalidateServiceCachesButton from 'components/services/InvalidateServiceCachesButton'
import OptionPageForm from 'components/services/OptionPageForm'
import ServiceActions from 'components/services/ServiceActions'
import ServiceBundler from 'components/services/ServiceBundler'
import ServiceConfigPrices from 'components/services/ServiceConfigPrices'
import ServiceCreditLimits from 'components/services/ServiceCreditLimits'
import TestimonialsList from 'components/testimonials/TestimonialsList'
import LocalizedServiceCard from 'features/localizedServiceMappings/LocalizedServiceCard'
import { getWebsiteHostnameByCountryCode } from 'misc/country'
import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import { Link } from 'react-router-dom'
import ServicesViewContainer from '../../containers/ServicesViewContainer'
import Card from '../Card'
import LinearLoader from '../LinearLoader'
import BackButton from '../ui/BackButton'
import EditImage from './EditImage'
import QuestionsList from './QuestionsList'
import Service from './Service'
import ServiceFileDropAction from './ServiceFileDropAction'
import ServicesSbis from './ServicesSbis'
import WizardImportAction from './WizardImportAction'

class ServiceView extends React.Component {
    constructor(props) {
        super(props)
        this.saveBannerImage = this.saveBannerImage.bind(this)
        this.onSignupImageLoad = this.onSignupImageLoad.bind(this)
        this.onSignupImageError = this.onSignupImageError.bind(this)
        this.state = {
            keywords_to_scrape: [],
            open_modal: false,
            signupImageExists: false,
            signupImageUrl: null,
            signupImageRefresh: null,
            signupImageExtensionIndex: 0,
            showImageOverlay: false,
        }
    }

    onSetKeywordsToScrape = (value) => {
        this.setState({ keywords_to_scrape: value })
    }

    onSetModal = (value) => {
        this.setState({ open_modal: value })
    }

    componentDidMount() {
        const { fetchService, serviceId } = this.props
        fetchService(serviceId)
    }

    componentDidUpdate(prevProps) {
        const { fetchService, serviceId, service } = this.props
        if (serviceId !== prevProps.serviceId) {
            fetchService(serviceId)
            // Reset image check when service changes
            this.setState({ signupImageExists: false, signupImageRefresh: null })
        }
        // Re-check image when service data loads
        if (service && !prevProps.service) {
            this.setState({ signupImageExists: false })
        }
    }

    saveBannerImage() {
        const { saveServiceImage, serviceId } = this.props
        return (file, context) => {
            return saveServiceImage(serviceId, file, context)
        }
    }

    onSignupImageLoad() {
        this.setState({ signupImageExists: true })
    }

    onSignupImageError() {
        this.setState({ signupImageExists: false })
    }

    getSignupImageUrl() {
        const { service } = this.props
        if (!service) return null
        const baseUrl = `${APP.S3_SERVICES()}${service.country_code.toLowerCase()}/img/signup/${service.url}`
        const refresh = this.state.signupImageRefresh ? `?t=${this.state.signupImageRefresh}` : ''
        return `${baseUrl}.png${refresh}`
    }

    warningText(text) {
        return (
            <PreIcon name="warning" red>
                <strong style={{ color: 'red', padding: '4px' }}>{text}</strong>
            </PreIcon>
        )
    }

    scheduleKeywordsOptions(filterableOptions, serviceId, includeSigned = 0) {
        const buttonTitle = includeSigned
            ? 'Schedule specific options (incl. unfilled claimed/signed)'
            : 'Schedule specific options'
        const modalName = includeSigned
            ? 'selected_keywords_options_two_signed'
            : 'selected_keywords_options_two'
        return (
            <>
                <Button onClick={() => this.onSetModal(modalName)}>
                    {buttonTitle} <Icon name="forum" />{' '}
                    {includeSigned ? <Icon name="warning" /> : ''}
                </Button>
                <Modal
                    close={() => this.onSetModal(false)}
                    maxWidth={600}
                    open={this.state.open_modal === modalName}>
                    <div>
                        <h3>Only these selected options will be activated via the keywords</h3>
                        <Divider />
                        <div>
                            <Checkbox
                                options={filterableOptions.map((a) => {
                                    return { value: a.option_id, name: a.value }
                                })}
                                onChangeValue={this.onSetKeywordsToScrape}
                                value={this.state.keywords_to_scrape || []}
                            />
                        </div>
                        <Divider />
                        <Row>
                            {includeSigned ? (
                                <>
                                    {this.warningText(
                                        'This will affect signed and claimed providers.',
                                    )}
                                    <Divider />
                                </>
                            ) : (
                                ''
                            )}
                            {this.state.keywords_to_scrape.length ? (
                                <Col x>
                                    {this.buttonScheduleKeywords(buttonTitle, [
                                        serviceId,
                                        includeSigned,
                                        this.state.keywords_to_scrape,
                                    ])}
                                </Col>
                            ) : (
                                'Select options'
                            )}
                        </Row>
                        <Divider />
                    </div>
                </Modal>
            </>
        )
    }

    confirmWarningButton(
        buttonText,
        buttonElement,
        warningText = 'This will affect signed and claimed providers.',
        modalTitle = 'Confirm?',
    ) {
        const modalName = buttonText.replace(/\W/g, '_')
        return (
            <>
                <Button onClick={() => this.onSetModal(modalName)}>
                    {buttonText} <Icon name="forum" /> <Icon name="warning" />
                </Button>
                <Modal
                    close={() => this.onSetModal(false)}
                    maxWidth={600}
                    open={this.state.open_modal === modalName}>
                    <div>
                        <h3>{modalTitle}</h3>
                        <Divider />
                        {this.warningText(warningText)}
                        <Divider />
                        <Row>{buttonElement}</Row>
                        <Divider />
                    </div>
                </Modal>
            </>
        )
    }

    buttonScheduleKeywords(name, params, action = 'schedule-keywords-task') {
        return (
            <a
                target="_blank"
                href={`/api/office/providers/${action}/${params.join('/')}`}
                rel="noreferrer">
                <Button>{name}</Button>
            </a>
        )
    }

    render() {
        const { serviceId, service, optionPages, onFilesRejected, serviceEntities } = this.props
        const filterableOptions =
            serviceEntities && serviceEntities.options
                ? Object.keys(serviceEntities.options)
                      .map((option_id) => {
                          const question_id = serviceEntities.options[option_id].question_id
                          const question = serviceEntities.questions[question_id]
                          return question.filterable && question.service_id === service.service_id
                              ? serviceEntities.options[option_id]
                              : false
                      })
                      .filter(Boolean)
                : []

        if (!service) {
            return <LinearLoader fixed />
        }
        const identifier =
            service.country_code === 'NL' ? service.url : service.url + '-' + service.country_code

        return (
            <div>
                <Divider l />
                <AutoRow>
                    <BackButton fallBack="/settings/services" />
                    <h1>{service.name} </h1>
                    {service.mainServiceOptionName && (
                        <Link to={`/settings/services/service/${service.mainServiceId}`}>
                            <h3>
                                {' '}
                                <Icon name="sync_alt" /> {service.mainServiceOptionName}
                            </h3>
                        </Link>
                    )}
                    {service.subServices && (
                        <div>
                            {service.subServices.split(',').map((items) => {
                                const [service, serviceId, optionName] = items.split('|')
                                return (
                                    <Link to={`/settings/services/service/${serviceId}`}>
                                        <AutoRow>
                                            <PreIcon
                                                tagName="span"
                                                icon="radio_button_checked"
                                                fontSize={16}>
                                                {optionName}
                                            </PreIcon>
                                            <PreIcon tagName="span" icon="sync_alt" fontSize={16}>
                                                <b>{service}</b>
                                            </PreIcon>
                                        </AutoRow>
                                    </Link>
                                )
                            })}
                        </div>
                    )}{' '}
                    <Link to={`/settings/services/service/${serviceId}/questions/`}>
                        <Button name="text">
                            <PreIcon primColor icon="dynamic_form">
                                Questionnaires
                            </PreIcon>
                        </Button>
                    </Link>
                    <InvalidateServiceCachesButton serviceId={service.service_id} />
                    <a
                        target="_blank"
                        href={`//${getWebsiteHostnameByCountryCode(
                            service.country_code,
                        )}/nederland/${service.url}/`}
                        rel="noreferrer">
                        <Button name="text">
                            <Icon name="open" />
                            Top10 page
                        </Button>
                    </a>
                    <div></div>
                    <Link to={`/settings/seo/services-places-contents/?service=${serviceId}`}>
                        <Button name="link">
                            <PreIcon icon="open_in_new_tab" primColor>
                                SEO content
                            </PreIcon>
                        </Button>
                    </Link>
                </AutoRow>
                <Divider />
                <Row>
                    <Col xs={5}>
                        <div className="grid grid-flow-col items-center">
                            <EditImage
                                src={`${APP.S3_SERVICES()}${service.country_code.toLowerCase()}/img/${
                                    service.url
                                }.webp`}
                                context="top10"
                                height={200}
                                bgPositionSize="center center/contain"
                                save={this.saveBannerImage()}
                                mime="image/webp"
                                onFilesRejected={onFilesRejected}>
                                <div className="text-xl font-bold">Webp image</div>
                                <div style={{ fontSize: '0.85em' }}>type: .webp</div>
                            </EditImage>
                            <EditImage
                                src={`${APP.S3_SERVICES()}${service.country_code.toLowerCase()}/img/${
                                    service.url
                                }_sm.webp`}
                                context="top10_sm"
                                height={200}
                                bgPositionSize="center center/contain"
                                save={this.saveBannerImage()}
                                mime="image/webp"
                                onFilesRejected={onFilesRejected}>
                                <div className="text-xl font-bold">Webp image SM</div>
                                <div style={{ fontSize: '0.85em' }}>(without text) type: .webp</div>
                            </EditImage>
                            <EditImage
                                src={`${APP.S3_SERVICES()}${service.country_code.toLowerCase()}/img/${
                                    service.url
                                }_xs.webp`}
                                context="top10_xs"
                                height={200}
                                bgPositionSize="center center/contain"
                                save={this.saveBannerImage()}
                                mime="image/webp"
                                onFilesRejected={onFilesRejected}>
                                <div className="text-xl font-bold">Webp image XS</div>
                                <div style={{ fontSize: '0.85em' }}>
                                    (without lines/text) type: .webp
                                </div>
                            </EditImage>
                            <EditImage
                                src={`${APP.S3_SERVICES()}${service.country_code.toLowerCase()}/symbol/${
                                    service.url
                                }.svg`}
                                context="symbol"
                                height={200}
                                bgPositionSize="center center/contain"
                                save={this.saveBannerImage()}
                                mime="image/svg+xml"
                                onFilesRejected={onFilesRejected}>
                                <div className="text-xl font-bold">Symbol</div>
                                <div style={{ fontSize: '0.85em' }}>(redesign icon) type .svg</div>
                            </EditImage>
                            <EditImage
                                src={`${APP.S3_SERVICES()}img/icon/${identifier}/icon.svg`}
                                context="icon"
                                height={200}
                                bgPositionSize="center center/contain"
                                save={this.saveBannerImage()}
                                mime="image/svg+xml"
                                onFilesRejected={onFilesRejected}>
                                <div className="text-xl font-bold">Icon</div>
                                <div style={{ fontSize: '0.85em' }}>(.svg)</div>
                            </EditImage>
                            <div style={{ maxWidth: '150px' }}>
                                <EditImage
                                    src={`${APP.S3_SERVICES()}img/xsell/${identifier}.png`}
                                    context="xsell"
                                    height={100}
                                    bgPositionSize="center center/contain"
                                    save={this.saveBannerImage()}
                                    mime="image/png"
                                    onFilesRejected={onFilesRejected}>
                                    <div className="text-xl font-bold">xsell email</div>
                                    <div style={{ fontSize: '0.85em' }}>
                                        height: 100px
                                        <br />
                                        type: .png
                                    </div>
                                </EditImage>
                            </div>
                        </div>
                        <Divider m />
                        {this.state.signupImageExists ? (
                            <div
                                style={{
                                    textAlign: 'center',
                                    marginBottom: '16px',
                                    position: 'relative',
                                    display: 'inline-block',
                                    width: '100%',
                                }}>
                                <div
                                    style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                    }}
                                    onMouseEnter={() => this.setState({ showImageOverlay: true })}
                                    onMouseLeave={() => this.setState({ showImageOverlay: false })}>
                                    <img
                                        key={`signup-display-${this.state.signupImageRefresh || 0}`}
                                        src={this.getSignupImageUrl()}
                                        alt="Sign Up Image"
                                        style={{
                                            maxHeight: '100px',
                                            width: 'auto',
                                            maxWidth: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                            margin: '0 auto',
                                        }}
                                        onError={(e) => {
                                            // Try other extensions
                                            const currentSrc = e.target.src.split('?')[0]
                                            const baseUrl = currentSrc.replace(
                                                /\.(webp|png|jpg|jpeg)$/,
                                                '',
                                            )
                                            const extensions = ['webp', 'png', 'jpg', 'jpeg']
                                            const currentExtension =
                                                currentSrc.match(/\.(webp|png|jpg|jpeg)$/)?.[1] ||
                                                'webp'
                                            const currentIndex =
                                                extensions.indexOf(currentExtension)
                                            const refresh = this.state.signupImageRefresh
                                                ? `?t=${this.state.signupImageRefresh}`
                                                : ''

                                            if (currentIndex < extensions.length - 1) {
                                                const nextExtension = extensions[currentIndex + 1]
                                                e.target.src = `${baseUrl}.${nextExtension}${refresh}`
                                            } else {
                                                // All extensions tried, hide image and show button
                                                this.setState({ signupImageExists: false })
                                            }
                                        }}
                                        onLoad={this.onSignupImageLoad}
                                    />
                                    {this.state.showImageOverlay && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(200, 200, 200, 0.5)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px',
                                            }}>
                                            <input
                                                ref={(input) =>
                                                    (this.signupImageReplaceInput = input)
                                                }
                                                type="file"
                                                accept="image/webp,image/png,image/jpeg"
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0]
                                                    if (file) {
                                                        try {
                                                            const uploadPromise =
                                                                this.saveBannerImage()(
                                                                    file,
                                                                    'signup',
                                                                )
                                                            if (
                                                                uploadPromise &&
                                                                typeof uploadPromise.then ===
                                                                    'function'
                                                            ) {
                                                                uploadPromise
                                                                    .then((_response) => {
                                                                        // Force refresh of the displayed image with new timestamp
                                                                        const refreshTime =
                                                                            Date.now()
                                                                        // First, hide the image to force a re-check
                                                                        this.setState({
                                                                            signupImageExists: false,
                                                                            signupImageRefresh:
                                                                                refreshTime,
                                                                            signupImageExtensionIndex: 0,
                                                                        })
                                                                        // Then after a delay, trigger the hidden image to re-check
                                                                        // This gives S3/CloudFront time to propagate
                                                                        setTimeout(() => {
                                                                            this.setState({
                                                                                signupImageRefresh:
                                                                                    refreshTime + 1, // Increment to force re-render
                                                                            })
                                                                        }, 1000) // Increased delay for S3/CloudFront propagation
                                                                    })
                                                                    .catch((err) => {
                                                                        console.error(
                                                                            'Error uploading signup image:',
                                                                            err,
                                                                        )
                                                                        alert(
                                                                            'Error uploading image: ' +
                                                                                (err.message ||
                                                                                    JSON.stringify(
                                                                                        err,
                                                                                    )),
                                                                        )
                                                                    })
                                                            } else {
                                                                console.error(
                                                                    'saveBannerImage did not return a promise:',
                                                                    uploadPromise,
                                                                )
                                                                alert(
                                                                    'Error: Upload function did not return a promise',
                                                                )
                                                            }
                                                        } catch (error) {
                                                            console.error(
                                                                'Error in onChange handler:',
                                                                error,
                                                            )
                                                            alert('Error: ' + error.message)
                                                        }
                                                    }
                                                    // Reset input so same file can be selected again
                                                    e.target.value = ''
                                                }}
                                            />
                                            <Button
                                                name="default"
                                                onClick={() => {
                                                    this.signupImageReplaceInput?.click()
                                                }}
                                                style={{ opacity: 0.75 }}>
                                                <PreIcon icon="image" primColor>
                                                    Replace Sign Up Image
                                                </PreIcon>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <AutoRow>
                                <input
                                    ref={(input) => (this.signupImageUploadInput = input)}
                                    type="file"
                                    accept="image/webp,image/png,image/jpeg"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) {
                                            try {
                                                const uploadPromise = this.saveBannerImage()(
                                                    file,
                                                    'signup',
                                                )
                                                if (
                                                    uploadPromise &&
                                                    typeof uploadPromise.then === 'function'
                                                ) {
                                                    uploadPromise
                                                        .then((_response) => {
                                                            // Force refresh of the displayed image with new timestamp
                                                            const refreshTime = Date.now()
                                                            // First, hide the image to force a re-check
                                                            this.setState({
                                                                signupImageExists: false,
                                                                signupImageRefresh: refreshTime,
                                                                signupImageExtensionIndex: 0,
                                                            })
                                                            // Then after a delay, trigger the hidden image to re-check
                                                            // This gives S3/CloudFront time to propagate
                                                            setTimeout(() => {
                                                                this.setState({
                                                                    signupImageRefresh:
                                                                        refreshTime + 1, // Increment to force re-render
                                                                })
                                                            }, 1000) // Increased delay for S3/CloudFront propagation
                                                        })
                                                        .catch((err) => {
                                                            console.error(
                                                                'Error uploading signup image:',
                                                                err,
                                                            )
                                                            alert(
                                                                'Error uploading image: ' +
                                                                    (err.message ||
                                                                        JSON.stringify(err)),
                                                            )
                                                        })
                                                } else {
                                                    console.error(
                                                        'saveBannerImage did not return a promise:',
                                                        uploadPromise,
                                                    )
                                                    alert(
                                                        'Error: Upload function did not return a promise',
                                                    )
                                                }
                                            } catch (error) {
                                                console.error('Error in onChange handler:', error)
                                                alert('Error: ' + error.message)
                                            }
                                        }
                                        // Reset input so same file can be selected again
                                        e.target.value = ''
                                    }}
                                />
                                <Button
                                    name="link"
                                    onClick={() => {
                                        this.signupImageUploadInput?.click()
                                    }}>
                                    <PreIcon icon="image" primColor>
                                        Upload Sign Up Image
                                    </PreIcon>
                                </Button>
                            </AutoRow>
                        )}
                        {/* Hidden image to check if signup image exists */}
                        {service && (
                            <img
                                key={`signup-check-${this.state.signupImageRefresh || 0}`}
                                src={this.getSignupImageUrl()}
                                alt=""
                                style={{ display: 'none' }}
                                onLoad={this.onSignupImageLoad}
                                onError={this.onSignupImageError}
                            />
                        )}
                        <Divider m />
                        <Card header="CRM" icon="assignment">
                            <Divider m />
                            <AutoRow>
                                <a
                                    target="_blank"
                                    href={
                                        '/api/office/providers/download-crm-sheet/' +
                                        serviceId +
                                        '?crm=1'
                                    }
                                    rel="noreferrer">
                                    <Button name="borderedPoppins">Sheet for import</Button>
                                </a>
                                <GenerateTop10EmailWidgetsButton serviceId={serviceId} />
                            </AutoRow>
                        </Card>
                        <Divider m />
                        <Card header="SBIs" icon="category" useToggle>
                            <Divider m />
                            <ServicesSbis serviceId={serviceId} />
                        </Card>
                    </Col>
                    <Col xs>
                        <Card header="Settings" icon="settings" useToggle>
                            <Divider m />
                            <Service service={service} entityId={serviceId} />
                        </Card>
                        <Divider m />
                        <Card header="Sign Up Image" icon="image" useToggle>
                            <Divider m />
                            <div style={{ textAlign: 'center' }}>
                                <img
                                    src={`${APP.S3_SERVICES()}${service.country_code.toLowerCase()}/img/signup/${
                                        service.url
                                    }.webp?t=${this.state.signupImageRefresh || ''}`}
                                    alt="Sign Up Image"
                                    style={{
                                        maxHeight: '100px',
                                        width: 'auto',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                    }}
                                    onError={(e) => {
                                        // Try other common extensions if webp fails
                                        const extensions = ['png', 'jpg', 'jpeg']
                                        const currentSrc = e.target.src.split('?')[0]
                                        const baseUrl = currentSrc.replace(/\.webp$/, '')
                                        const refreshParam = this.state.signupImageRefresh
                                            ? `?t=${this.state.signupImageRefresh}`
                                            : ''
                                        let attemptIndex = 0
                                        const tryNextExtension = () => {
                                            if (attemptIndex < extensions.length) {
                                                e.target.src = `${baseUrl}.${extensions[attemptIndex]}${refreshParam}`
                                                attemptIndex++
                                            } else {
                                                e.target.style.display = 'none'
                                            }
                                        }
                                        e.target.onerror = tryNextExtension
                                        tryNextExtension()
                                    }}
                                />
                            </div>
                        </Card>
                        <Divider m />
                        <Card header="Testimonial" icon="thumb_up" useToggle>
                            <Divider m />
                            <TestimonialsList
                                filterId={`f_${serviceId}`}
                                options={{ service_id: serviceId }}
                            />
                        </Card>
                        <Divider m />
                        <Card header="FAQs" icon="contact_support" useToggle>
                            <Divider m />
                            <FaqsList
                                filterId={`f_${serviceId}`}
                                service={service}
                                options={{ service_id: serviceId }}
                            />
                        </Card>
                        <Divider m />
                        <Card header="Xsell (Demand-facing)" icon="addchart">
                            <Divider m />
                            <CrossSellOptionsDropdown serviceId={serviceId} />
                            <Divider m />
                            <ServiceBundler serviceId={serviceId} type={'Xsell'} />
                        </Card>
                        <Divider m />
                        <Card header="Related Services (Supply-facing)" icon="addchart">
                            <Divider m />
                            <ServiceBundler serviceId={serviceId} type={'Related'} />
                        </Card>
                        {optionPages.map((optionPage, i) => {
                            return (
                                <Fragment key={i}>
                                    <Divider m />
                                    <Card
                                        useToggle
                                        icon={optionPage.keyword_singular ? 'filter' : 'fiber_new'}
                                        header={`Optionpage: ${
                                            optionPage.keyword_singular || 'NEW'
                                        }`}>
                                        <Divider m />
                                        <OptionPageForm
                                            serviceId={serviceId}
                                            id={optionPage.option_page_id}
                                        />
                                        <Divider m />
                                        <b>Faqs</b>
                                        <Divider />
                                        <FaqsList
                                            filterId={`f_${serviceId}_${optionPage.option_id}`}
                                            options={{
                                                service_id: serviceId,
                                                option_id: optionPage.option_id,
                                            }}
                                        />
                                    </Card>
                                </Fragment>
                            )
                        })}
                        <Divider />
                        <AddOptionPageButton serviceId={serviceId} />
                        <Divider />
                        <Divider m />
                        <LocalizedServiceCard service={service} />
                    </Col>
                </Row>
                <Divider m />
                <style>{`
                    .cta-i {
                        transition: transform 0.2s ease 0s;
                    }
                    
                    .ctas.open .cta-i {
                        transform: scaleX(-1);
                    }
                `}</style>
                <Card
                    className="ctas"
                    classNameIcon="cta-i"
                    header="CTAs"
                    icon="sports_martial_arts"
                    useToggle>
                    <Divider m />
                    <CtasList serviceId={parseInt(serviceId, 10)} />
                </Card>
                <Divider m />
                <Card header="Prices" icon="euro_symbol" useToggle>
                    <Divider m />
                    <ServiceConfigPrices serviceId={serviceId} />
                </Card>
                <Divider m />
                <Card header="Credit Limits" icon="credit_card_off" useToggle>
                    <Divider m />
                    <ServiceCreditLimits serviceId={parseInt(serviceId, 10)} />
                </Card>
                <Divider m />
                <Card header="Questionnaires" icon="question_answer">
                    <Divider m />
                    <QuestionsList serviceId={serviceId} />
                </Card>
                <Divider xl />
                <Card header="Keywords" useToggle icon="🧖🏻">
                    <Divider m />
                    <KeywordsList
                        filterId={`f_${serviceId}`}
                        options={{ service_id: serviceId }}
                        serviceId={serviceId}
                    />
                    <Divider m />
                    <Card header={'Import keywords'} useToggle icon={'assignment'}>
                        <ServiceFileDropAction
                            serviceId={serviceId}
                            buttonText={'import keywords file'}
                            text="The file must be csv or xlsx and contain the following in the filename: 'keyword'.
                            Columns must have 'keyword' 'service_id' 'category' 'services_keywords_variations'.
                            Services keywords variations should be separated by comma, and will overwrite existing variations.
                            You can add 'option_id' column with the option id or value to assign the keyword to an option."
                            action={'services-keywords-import'}
                        />
                        <a
                            target="_blank"
                            rel="noreferrer"
                            href="/api/office/services/services-keywords-import-template/">
                            <Button name="borderedPoppins">Example template</Button>
                        </a>
                    </Card>
                    <Divider m />
                    <Card header="Tasks" useToggle icon="assignment">
                        <Divider m />
                        {this.buttonScheduleKeywords('Schedule keywords', [serviceId])}{' '}
                        {this.confirmWarningButton(
                            'Schedule keywords (incl. unfilled claimed/signed)',
                            this.buttonScheduleKeywords(
                                'Schedule keywords (incl. unfilled claimed/signed)',
                                [serviceId, 1],
                            ),
                        )}
                        <Divider m />
                        {this.scheduleKeywordsOptions(filterableOptions, serviceId, 0)}{' '}
                        {this.scheduleKeywordsOptions(filterableOptions, serviceId, 1)}
                        <Divider m />
                        {this.buttonScheduleKeywords(
                            'Set defaults',
                            [serviceId],
                            'set-default-keywords',
                        )}{' '}
                        {this.confirmWarningButton(
                            'Set defaults (include signed)',
                            this.buttonScheduleKeywords(
                                'Set defaults (include signed)',
                                [serviceId, 1],
                                'set-default-keywords',
                            ),
                        )}
                    </Card>
                </Card>
                <Divider m />
                <Card header={'Actions'} useToggle icon={'assignment'}>
                    <Divider m />
                    <ServiceActions serviceId={serviceId} />
                    <Divider m />
                    <Card header="Questionnaires wizard import" useToggle icon="upload">
                        <WizardImportAction serviceId={parseInt(serviceId, 10)} />
                    </Card>
                    <Divider m />
                    <Card header={'scrape'} useToggle icon={'assignment'}>
                        <ServiceFileDropAction
                            serviceId={serviceId}
                            buttonText={'scrape file'}
                            text="The file must be csv and contain the following in the filename: 'url_scrape'"
                            action={'schedule-scrape'}
                        />
                    </Card>
                    <Divider m />
                    <Card header={'import'} useToggle icon={'assignment'}>
                        <ServiceFileDropAction
                            serviceId={serviceId}
                            buttonText={'import file'}
                            text="The file must be xlsx and contain the following in the filename: 'import', the service_id, the service_url, and the service country_code"
                            action={'schedule-import'}
                        />
                    </Card>
                </Card>
                <Divider m />
                <Divider xl />
                <Card header="Logs" icon="🪵">
                    <Divider m />
                    <SystemLogsList
                        filterId={`systemlogs_${serviceId}`}
                        options={{
                            relation: 'services',
                            foreignKey: serviceId,
                        }}
                    />
                </Card>
                <Divider xxxl />
                <Divider xxxl />
            </div>
        )
    }
}

ServiceView.propTypes = {
    serviceId: PropTypes.string,

    service: PropTypes.object,
    serviceEntities: PropTypes.object,
    fetchService: PropTypes.func,
}

export default ServicesViewContainer(ServiceView)
